import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../root/RootReducer";
import { Group, UpdateUserList, UserSkill } from "../../user/User";
import { GetUserInfoList, UpdateUserInfo, ImportUsersStarted } from "../../user/UserActions";
import styles from "./EditUser.module.css";
import {
    Modal,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    CircularProgress,
    FilledInput,
    InputAdornment,
    Switch,
    FormControlLabel,
    Button,
    createStyles,
    makeStyles,
    Theme,
} from "@material-ui/core";
import SelectSkill from "./SelectSkill";
import { filterUserList } from "../../presenters/PresentersActions";
import { GetRecommendProfessionalList } from "../../common/homePage/HomePageActions";
import { uniq, isEmpty, GetSkillIdsFromSkillList, isNull } from "../../utils/ObjectUtils";
import PuiMessage from "../../components/widgets/PuiMessage";
import SelectGroup from "../../pages/admin/EditUser/SelectGroup";
import EditUserTableRow from "./EditUserTableRow";
import { Close, Search } from "@material-ui/icons";
import { roles } from "../../utils/LogicUtils";
import PuiTypography from "../../components/elements/PuiTypography";
import getAxiosInstance from "../../common/mhiAxios";
import { t } from "i18next";
import { toNumber } from "lodash";

// 表示用データ定義
interface UserData {
    id: string;
    imageUrl: string;
    imageFile: any;
    fullName: string;
    email: string;
    localAccountId: string;
    organization: string;
    lastLogin: string;
    skills: number[];
    skillList: UserSkill[];
    isAdmin: boolean;
    isEditor: boolean;
    isProf: boolean;
    isGuest: boolean;
    isUnavailable: boolean;
    group: Group[];
    loginIn45days: boolean;
}

interface EditUserProps {
    setIsSaved: (isSaved: boolean) => void;
    isSaved: boolean;
}

export default function EditUser({ setIsSaved, isSaved }: EditUserProps) {
    const dispatch = useDispatch();

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            searchInput: {
                height: "40px",
                fontSize: "14px",
                borderRadius: "5px",
                fontFamily: "noto-sans-cjk-jp",
                color: "var(--white)",
                marginTop: "5px",
                marginBottom: "15px",
                border: "var(--gray-38) 1.0px solid",
                transition: "all 0.2s;",
                width: "160px",
                "&.Mui-focused": {
                    width: "240px",
                    border: "#4395ea 1.6px solid",
                    "& input::placeholder": {
                        color: "transparent",
                    },
                    "& #searchIconButton": {
                        color: "var(--white)",
                    },
                },
                "&:hover": {
                    backgroundColor: "var(--textfield-bg)",
                },
            },
        })
    );

    const classes = useStyles();

    //APIコール
    useEffect(() => {
        dispatch(GetUserInfoList());
    }, [dispatch]);

    // バックエンドからユーザデータ取得
    const { skillList, initialUserList, isLoading, errorMessage, userInfo } = useSelector(
        (state: AppState) => ({
            skillList: state.user.skillList,
            initialUserList: state.user.userInfoList,
            isLoading: state.user.isLoading,
            errorMessage: state.user.errorMessage,
            userInfo: state.user.userInfo,
        })
    );

    // 表示用データ
    // eslint-disable-next-line
    const [userData, ChangeUserData] = useState<UserData[]>([]);

    // メッセージ制御用
    const [showMessage, setShowMessage] = useState<boolean>(false);

    // 会社名選択
    const [companyList, ChangeCompanyList] = useState<string[]>(["会社を選択"]);
    // const [visibleCompany, ChangeVisibleCompany] = useState("その他");
    // const [isVisibleHeader, ChangeVisibleHeader] = useState(false);
    const PushCompanyName = (organization: string) => {
        companyList.push(organization);
        const tempList = uniq(companyList);
        // 会社リストの並び替え
        tempList.sort(function (a, b) {
            if (a === "会社を選択" || b === "会社を選択") return 0;
            if (a === "その他" && b === "未登録") return -1;
            if (a.toString().length < b.toString().length) {
                return 1;
            } else if (a.toString().length > b.toString().length) {
                return -1;
            }
            return 0;
        });
        ChangeCompanyList(tempList);
        // 会社名にその他が存在しない場合
        // if (!tempList.includes("その他")) {
        //     ChangeVisibleCompany("会社を選択");
        // }
    };

    // const ChooseCompany = (organization: string) => {
    //     ChangeVisibleCompany(organization);
    // };

    // ユーザー管理画面をリフレッシュ
    // const refresh = () => {
    //     dispatch(StartSyncBatch())
    //     // TODO: temp
    //     // プロフェショナル一覧画面のrefresh
    //     const userFilter = {id:-1, skill:[], tag:[]}
    //     setTimeout(() => dispatch(filterUserList(userFilter)), 2000);
    //     setTimeout(() => dispatch(GetRecommendProfessionalList()), 2000);
    //     setShowMessage(true);
    // };
    // メッセージを閉じる
    const closeMsg = () => {
        setShowMessage(false);
    };

    // バックエンドデータから表示用データに変換
    const [isConvertDisplayData, ConvertDisplayDataFlag] = useState(false);
    initialUserList !== undefined &&
        isConvertDisplayData === false &&
        initialUserList.map(function (row) {
            if (Object.keys(userData).length < Object.keys(initialUserList).length) {
                userData.push({
                    id: row.id,
                    imageUrl: row.imageUrl,
                    imageFile: null,
                    fullName: row.fullName ?? "",
                    email: row.email,
                    localAccountId: row.localAccountId,
                    organization: row.organization,
                    lastLogin: row.lastLogin,
                    skills: row.skills || GetSkillIdsFromSkillList(row.skillList),
                    skillList: row.skillList,
                    group: row.group,
                    isAdmin: row.roles.includes("ADMIN"),
                    isEditor: row.roles.includes("EDITOR"),
                    isProf: row.roles.includes("PRESENTER"),
                    isGuest: row.roles.includes("GUEST"),
                    isUnavailable: row.roles.includes("UNAVAILABLE"),
                    loginIn45days: row.loginIn45days,
                });
                // 会社名をセット
                PushCompanyName(row.organization);
            }
            ConvertDisplayDataFlag(true);
            return null;
        });
    const [filterUserDateList, setFilterUserDataList] = useState<UserData[]>([]);
    const [swtichActiveUser, setSwtichActiveUser] = useState<boolean>(false);

    useEffect(() => {
        setFilterUserDataList(userData);
    }, [userData]);

    // プロファイル変更カウント
    const [profileEditNumber, CountProfileEdit] = useState(0);
    const [isLimitProfile, ChangeLimitProfile] = React.useState(false);

    useEffect(() => {
        if (profileEditNumber > 3) {
            ChangeLimitProfile(true);
        } else {
            ChangeLimitProfile(false);
        }
    }, [profileEditNumber]);

    // プロファイルコンポーンネントコールバック
    const ChangeProfileImage = (changeUserId: string, newImageUrl: string, newImageFile: any) => {
        const changeIndex = handleGetIndexByUserId(toNumber(changeUserId), userData);
        if (isNull(changeIndex) || changeIndex === undefined) return;
        userData[Number(changeIndex)]["imageUrl"] = "";
        userData[Number(changeIndex)]["imageUrl"] = newImageUrl;
        userData[Number(changeIndex)]["imageFile"] = newImageFile;
        CountProfileEdit(profileEditNumber + 1);
        setIsSaved(false);
    };

    // ユーザ権限変更
    const ChangeRole = (changeUserId: number, role: string) => {
        const changeIndex = handleGetIndexByUserId(changeUserId, userData);
        if (isNull(changeIndex) || changeIndex === undefined) return;
        userData[changeIndex]["isAdmin"] = roles.indexOf(role) === 0 ? true : false;
        userData[changeIndex]["isEditor"] = roles.indexOf(role) === 1 ? true : false;
        setIsSaved(false);
    };

    // ユーザProfessional権限変更
    const ChangeProf = (changeUserId: number, changeValue: boolean) => {
        const changeIndex = handleGetIndexByUserId(changeUserId, userData);
        if (isNull(changeIndex) || changeIndex === undefined) return;

        userData[changeIndex]["isProf"] = !changeValue;
        setIsSaved(false);
    };

    // ユーザ利用権限変更
    const ChangeAvailable = (changeUserId: number, changeValue: boolean) => {
        const changeIndex = handleGetIndexByUserId(changeUserId, userData);
        if (isNull(changeIndex) || changeIndex === undefined) return;

        userData[changeIndex]["isUnavailable"] = !changeValue;
        setIsSaved(false);
    };

    // ユーザGuest権限変更
    // const ChangeGuest =(changeIndex: number, changeValue: boolean) => {
    //     userData[changeIndex]["isGuest"] = !changeValue;
    //     setIsSaved(false);
    // };

    // ユーザーリスト一覧内のuserIdからindexを取得する
    const handleGetIndexByUserId = (userId: number, list: any[]) => {
        if (!userId || !list || list.length === 0) return;
        const index = list.findIndex((item) => Number(item.id) === userId);
        return index;
    };

    // --------------------スキル&グループ変更モーダル--------------------

    const [editUserId, SetEditUserId] = React.useState("-1");
    const [editUserName, SetEditUserName] = React.useState("");
    const [editUserSkills, ChangeEditUserSkills] = useState([""]);

    // スキル選択モーダル画面表示制御
    const [isOpenSkillSelectModal, ShowSkillSelectModal] = useState(false);
    const OpenSkillSelectModal = (openUserId: string, openUserName: string) => {
        ShowSkillSelectModal(true);
        const userSkillLists =
            userData[userData.findIndex(({ id }) => id === openUserId)]["skillList"];
        const userSkills = userSkillLists.map(function (row) {
            return row.skill;
        });

        ChangeEditUserSkills(userSkills);
        SetEditUserId(openUserId);
        SetEditUserName(openUserName);
    };

    const CloseSkillSelectModal = () => {
        ShowSkillSelectModal(false);
        ChangeEditUserSkills([""]);
    };

    const SetUserSkillIds = (updateSkillIds: number[]) => {
        const changeIndex = userData.findIndex(({ id }) => id === editUserId);
        const skillNames: string[] = ChangeSkillIdsToSkillNames(updateSkillIds);
        const userSkillList: UserSkill[] = updateSkillIds.map((updateSkillId, index) => {
            return {
                id: updateSkillId,
                skill: skillNames[index],
            };
        });
        userData[changeIndex]["skills"] = updateSkillIds;
        userData[changeIndex]["skillList"] = userSkillList;
        ChangeEditUserSkills(skillNames);
        // setIsSaved(false);
    };

    // スキルIDからスキル名を検索する
    const FindSkillNameBySkillId = (skillId: number) => {
        const skill = skillList.find((row) => row.id === skillId);
        return skill?.skill;
    };

    // スキルIDの配列からスキル名の配列に変換する
    const ChangeSkillIdsToSkillNames = (skillIds: number[]) => {
        const skillNames: string[] = [];
        for (const index in skillIds) {
            const skillName = FindSkillNameBySkillId(Number(skillIds[index]));
            if (skillName && typeof skillName !== "undefined") skillNames.push(skillName);
        }
        return skillNames;
    };

    // グループ選択モーダル画面表示制御
    const [isOpenGroupSelectModal, ShowGroupSelectModal] = useState(false);
    const [editUserGroup, ChangeEditUserGroup] = useState<Group[]>([]);

    const OpenGroupSelectModal = (openUserId: string, openUserName: string) => {
        ShowGroupSelectModal(true);
        const targetGroup = userData.find(({ id }) => id === openUserId)?.group;
        if (targetGroup) ChangeEditUserGroup(targetGroup);
        SetEditUserId(openUserId);
        SetEditUserName(openUserName);
    };

    const CloseGroupSelectModal = () => {
        ShowGroupSelectModal(false);
        ChangeEditUserGroup([]);
    };

    const SetUserGroup = (updateGroupList: Group[]) => {
        const changeIndex = userData.findIndex(({ id }) => id === editUserId);
        userData[changeIndex]["group"] = updateGroupList;
        ChangeEditUserGroup(updateGroupList);
        setIsSaved(false);
    };

    // ユーザ更新ボタン押下
    const comparisonUserData: UpdateUserList[] = [];
    const updateUserData: UpdateUserList[] = [];
    const sendUserData: UpdateUserList[] = [];
    const ClickUpdateButton = () => {
        // バックエンドのユーザデータ変換
        initialUserList &&
            initialUserList.map(function (row) {
                comparisonUserData.push({
                    id: row.id,
                    imageFile: null,
                    fullName: row.fullName,
                    skills: row.skills,
                    skillList: row.skillList,
                    roles: row.roles,
                    group: row.group,
                });
                return null;
            });

        // 変更後のユーザデータ変換
        userData.map(function (row) {
            const role: string[] = [];

            if (row.isAdmin === true) {
                role.push("ADMIN");
            }
            if (row.isEditor === true) {
                role.push("EDITOR");
            }
            if (row.isProf === true) {
                role.push("PRESENTER");
            }
            if (row.isGuest === true) {
                role.push("GUEST");
            }
            if (row.isUnavailable === true) {
                role.push("UNAVAILABLE");
            }

            updateUserData.push({
                id: row.id,
                imageFile: row.imageFile,
                fullName: row.fullName,
                skills: row.skills,
                skillList: row.skillList,
                roles: role,
                group: row.group,
            });

            return null;
        });

        // 比較
        updateUserData.forEach(function (row, index) {
            if (
                JSON.stringify(row.skills) !==
                    JSON.stringify(comparisonUserData[index]["skills"]) ||
                JSON.stringify(row.roles) !== JSON.stringify(comparisonUserData[index]["roles"]) ||
                JSON.stringify(row.group) !== JSON.stringify(comparisonUserData[index]["group"]) ||
                JSON.stringify(row.imageFile) !==
                    JSON.stringify(comparisonUserData[index]["imageFile"])
            ) {
                sendUserData.push({
                    id: row.id,
                    imageFile: row.imageFile,
                    fullName: row.fullName,
                    skills: row.skills,
                    skillList: row.skillList,
                    roles: row.roles,
                    group: row.group,
                });
            }
            return null;
        });
        dispatch(UpdateUserInfo(sendUserData));
        ConvertDisplayDataFlag(false);
        setIsSaved(true);
        CountProfileEdit(0);
        ChangeLimitProfile(false);
        // TODO: temp
        // プロフェショナル一覧画面のrefresh
        if (userInfo) {
            const userFilter = { id: parseInt(userInfo.id), skill: [], tag: [] };
            setTimeout(() => dispatch(filterUserList(userFilter)), 2000);
            setTimeout(() => dispatch(GetRecommendProfessionalList(parseInt(userInfo.id))), 2000);
        }
    };
    const handleClickCSVExport = () => {
        const currentAccessToken = localStorage.getItem("cineonhub_access_token");
        getAxiosInstance()
            .get(
                process.env.REACT_APP_API_ENDPOINT +
                    "/api/getUserInfoCsv?isActive=" +
                    swtichActiveUser,
                {
                    headers: {
                        Authorization: "Bearer " + currentAccessToken,
                        responseType: "blob",
                    },
                }
            )
            .then((res) => {
                let bom = new Uint8Array([0xef, 0xbb, 0xbf]);
                let blob = new Blob([bom, res.data], { type: "text/csv" });
                let url = String([URL.createObjectURL(blob)]);
                // リンクを作成してダウンロードする。
                let link = document.createElement("a");
                link.href = url;
                link.download = "UserInfoResult.csv";
                link.click();
            });
    };
    const inputRef = useRef<HTMLInputElement>(null);
    const ImportUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.length === 0) {
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        } else if (event.target.files?.length === 1) {
            if (userInfo) {
                dispatch(ImportUsersStarted(event.target.files[0], parseInt(userInfo.id)));
            }
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        } else {
            if (inputRef.current) {
                inputRef.current.value = "";
            }
        }
    };
    const handleClickCSVImport = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const [inputvalue, setInputValue] = useState("");
    const tableRef = useRef<HTMLTableElement | null>(null);

    // user検索
    const searchUser = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        const inputValue = event.target.value.trim();
        const filterResult = userData.filter((user) => {
            return user.email.includes(inputValue) || user.fullName.includes(inputValue);
        });
        setFilterUserDataList(filterResult);
        if (!tableRef || !tableRef.current) return;
        tableRef.current.scrollTop = 0;
    };

    // 検索欄の入力値消去
    const deleteInput = () => {
        setInputValue("");
        setFilterUserDataList(userData);
    };

    const SwitchVisibleActiveUser = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSwtichActiveUser(event.target.checked);
        if (event.target.checked) {
            const filterResult = userData.filter((user) => {
                return user.loginIn45days === true;
            });
            setFilterUserDataList(filterResult);
        } else {
            setFilterUserDataList(userData);
        }
        if (!tableRef || !tableRef.current) return;
        tableRef.current.scrollTop = 0;
    };

    // 表示待機
    if (isLoading) {
        return (
            <div className={styles.noContent}>
                <CircularProgress size={60} style={{ color: "var(--loading-bg)" }} />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {/* ユーザ追加ボタン */}
            {/* <IconButton><AddCircleRoundedIcon className={styles.addButton} style={{width:"40px", height:"40px",color:"#f50057"}}/></IconButton> */}

            {/* <div className={styles.selectCompany}> */}
            {/* <PuiSelect
                    defaultValue={visibleCompany}
                    childrens={companyList}
                    onChange={ChooseCompany}
                /> */}
            {/* <div className={styles.refreshButton} data-for="batch" data-tip={"ユーザー同期バッチを実行"}>
                    <PuiFloatButton children={[<RefreshIcon/>]} size="medium" onClick={refresh}/>
                </div> */}
            {/* <PuiTooltip id="batch"/> */}
            {/* </div> */}
            <PuiTypography size={24} color={"white"}>
                {t("ユーザ管理")}
            </PuiTypography>

            {/* ユーザ一覧リスト */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <FilledInput
                    placeholder={String(t("ユーザ検索"))}
                    onChange={searchUser}
                    disableUnderline={true}
                    autoComplete={"off"}
                    value={inputvalue}
                    className={`${styles.searchInput} ${classes.searchInput}`}
                    endAdornment={
                        <>
                            {inputvalue.length === 0 ? (
                                <InputAdornment position="start">
                                    <Search
                                        id="searchIconButton"
                                        className={styles.searchIconButton}
                                    ></Search>
                                </InputAdornment>
                            ) : (
                                <InputAdornment position="start">
                                    <Button
                                        className={styles.searchCloseButton}
                                        onClick={deleteInput}
                                        disableRipple={true}
                                    >
                                        <Close />
                                    </Button>
                                </InputAdornment>
                            )}
                        </>
                    }
                />
            </div>
            {
                <TableContainer
                    ref={tableRef}
                    className={styles.table}
                    style={{ overflowX: "hidden" }}
                    component={Paper}
                >
                    <Table stickyHeader style={{ overflowX: "hidden" }}>
                        <TableHead>
                            <TableRow style={{ width: "100%" }}>
                                {/* <TableCell style={{width:"40px",borderBottom: 0}}></TableCell> */}
                                <TableCell
                                    style={{
                                        backgroundColor: "var(--textfield-bg)",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "55px",
                                        }}
                                    ></div>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontSize: "10.5px",
                                        whiteSpace: "nowrap",
                                        fontFamily: "noto-sans-cjk-jp",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            width: "170px",
                                            minWidth: "170px",
                                            maxWidth: "170px",
                                            marginLeft: "-42px",
                                        }}
                                    >
                                        {t("名前")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontSize: "10.5px",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        whiteSpace: "nowrap",
                                        fontFamily: "noto-sans-cjk-jp",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            width: "150px",
                                            minWidth: "280px",
                                            maxWidth: "280px",
                                            marginLeft: "20px",
                                        }}
                                    >
                                        {t("メールアドレス")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        width: "20%",
                                        fontSize: "10.5px",
                                        textAlign: "center",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        whiteSpace: "nowrap",
                                        fontFamily: "noto-sans-cjk-jp",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            textAlign: "center",
                                        }}
                                    >
                                        {t("スキル")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        width: "20%",
                                        fontSize: "10.5px",
                                        textAlign: "center",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        whiteSpace: "nowrap",
                                        fontFamily: "noto-sans-cjk-jp",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            textAlign: "center",
                                        }}
                                    >
                                        {t("グループ")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        width: "20%",
                                        fontSize: "10.5px",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            margin: "0",
                                            fontFamily: "noto-sans-cjk-jp",
                                        }}
                                    >
                                        {t("役割")}
                                    </h2>
                                </TableCell>
                                {/* <TableCell
                                    align="center"
                                    style={{ width: "8%", fontSize: "10.5px", borderBottom: 0 }}
                                >
                                    <h2
                                        style={{
                                            width: "70px",
                                            minWidth: "70px",
                                            maxWidth: "70px",
                                            fontFamily: "noto-sans-cjk-jp",
                                        }}
                                    >
                                        Admin.
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{ width: "8%", fontSize: "10.5px", borderBottom: 0 }}
                                >
                                    <h2
                                        style={{
                                            width: "70px",
                                            minWidth: "70px",
                                            maxWidth: "70px",
                                            fontFamily: "noto-sans-cjk-jp",
                                        }}
                                    >
                                        Editor.
                                    </h2>
                                </TableCell> */}
                                <TableCell
                                    align="center"
                                    style={{
                                        width: "8%",
                                        fontSize: "10.5px",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            width: "115px",
                                            minWidth: "115px",
                                            maxWidth: "115px",
                                            fontFamily: "noto-sans-cjk-jp",
                                        }}
                                    >
                                        {t("プレゼンター")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        width: "8%",
                                        fontSize: "10.5px",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            width: "73px",
                                            minWidth: "73px",
                                            maxWidth: "73px",
                                            fontFamily: "noto-sans-cjk-jp",
                                            borderRight: "solid 0.5px #FFFFFF0D",
                                        }}
                                    >
                                        {t("利用可能")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{
                                        fontSize: "10.5px",
                                        textAlign: "center",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        whiteSpace: "nowrap",
                                        fontFamily: "noto-sans-cjk-jp",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            width: "130px",
                                            minWidth: "130px",
                                            maxWidth: "130px",
                                        }}
                                    >
                                        {t("最終アクセス")}
                                    </h2>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    style={{
                                        width: "8%",
                                        fontSize: "10.5px",
                                        borderBottom: "solid 0.5px #FFFFFF0D",
                                        color: "white",
                                        backgroundColor: "var(--textfield-bg)",
                                        borderRight: "solid 0.5px #FFFFFF0D",
                                    }}
                                >
                                    <h2
                                        style={{
                                            width: "100px",
                                            minWidth: "100px",
                                            maxWidth: "100px",
                                            fontFamily: "noto-sans-cjk-jp",
                                        }}
                                    >
                                        {t("活動")}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={swtichActiveUser}
                                                    onChange={SwitchVisibleActiveUser}
                                                    name="activeUser"
                                                    color="secondary"
                                                />
                                            }
                                            label={""}
                                            labelPlacement="start"
                                        />
                                    </h2>
                                </TableCell>
                                {/* <TableCell align="center" style={{minWidth:"8%",fontSize:"10.5px",borderBottom: 0}}><h2>Guest</h2></TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filterUserDateList.length > 0 &&
                                filterUserDateList.map((row, index) => (
                                    <EditUserTableRow
                                        key={row.id}
                                        userId={Number(row.id)}
                                        user={row}
                                        ChangeProfileImage={ChangeProfileImage}
                                        ChangeRole={ChangeRole}
                                        ChangeProf={ChangeProf}
                                        OpenGroupSelectModal={OpenGroupSelectModal}
                                        OpenSkillSelectModal={OpenSkillSelectModal}
                                        ChangeAvailable={ChangeAvailable}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
            {filterUserDateList.length < 1 && (
                <div className={styles.noResultDiv}> {t("ユーザーが見つかりません")}</div>
            )}

            {/* 更新ボタン */}

            {isLimitProfile && (
                <p className={styles.attention}>
                    {t(
                        "※一度に多くのプロフィール画像を更新すると、更新に時間がかかる可能性があります"
                    )}
                </p>
            )}
            {/* <Button size="small" variant="outlined" color="secondary" onClick={ClickUpdateButton}className={styles.updateButton} style={{position:"absolute"}}  disabled={isSaved}>更新</Button> */}
            <div className={styles.uploadButton}>
                {
                    <input
                        style={{ display: "none" }}
                        ref={inputRef}
                        multiple
                        type="file"
                        accept=".csv"
                        onChange={ImportUsers}
                    />
                }
                {
                    <Button
                        id="importButton"
                        type="submit"
                        onClick={handleClickCSVImport}
                        className={styles.importButton}
                    >
                        {t("インポート")}
                    </Button>
                }
                {
                    <Button
                        id="exportButton"
                        type="submit"
                        onClick={handleClickCSVExport}
                        className={styles.exportButton}
                    >
                        {t("エクスポート")}
                    </Button>
                }
            </div>
            <div className={styles.updateButton}>
                {isSaved ? (
                    <Button
                        disabled={isSaved}
                        type="submit"
                        onClick={ClickUpdateButton}
                        color="secondary"
                        className={styles.addButton}
                        style={{
                            color: "var(--loading-bg)",
                            fontSize: "16px",
                            fontFamily: "noto-sans-cjk-jp,sans-serif",
                            backgroundColor: "var(--button-bg)",
                        }}
                    >
                        {t("更新")}
                    </Button>
                ) : (
                    <Button
                        type="submit"
                        onClick={ClickUpdateButton}
                        color="secondary"
                        className={styles.addButton}
                        style={{
                            color: "white",
                            fontSize: "16px",
                            fontWeight: "bold",
                            fontFamily: "noto-sans-cjk-jp,sans-serif",
                            backgroundColor: "var(--studio-btn-bg)",
                        }}
                    >
                        {t("更新")}
                    </Button>
                )}
            </div>

            {/* スキル選択モーダル */}
            <Modal open={isOpenSkillSelectModal} onClose={CloseSkillSelectModal}>
                <SelectSkill
                    userName={editUserName}
                    in={isOpenSkillSelectModal}
                    onClose={CloseSkillSelectModal}
                    userSkills={editUserSkills}
                    SetUserSkillIds={SetUserSkillIds}
                    SetIsSaved={setIsSaved}
                />
            </Modal>

            {/* グループ選択モーダル */}
            <Modal open={isOpenGroupSelectModal} onClose={CloseGroupSelectModal}>
                <SelectGroup
                    targetName={editUserName}
                    visibleModal={isOpenGroupSelectModal}
                    onClose={CloseGroupSelectModal}
                    userGroup={editUserGroup}
                    SetGroupList={SetUserGroup}
                    SetIsSaved={setIsSaved}
                />
            </Modal>

            <PuiMessage
                open={showMessage}
                type={isEmpty(errorMessage) ? "success" : "error"}
                title={isEmpty(errorMessage) ? t("同期成功しました") : errorMessage}
                onClose={closeMsg}
            />
        </div>
    );
}
